import { Link } from "react-router-dom";
import { ArrowRight, Plane, Landmark, Briefcase, Gem } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../constants/site";
import Reveal from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";

const SERVICE_ICONS = { airport: Plane, eu: Landmark, corporate: Briefcase, events: Gem };
const SERVICE_IMAGES = { airport: IMAGES.front, eu: IMAGES.wheel, corporate: IMAGES.chauffeur, events: IMAGES.brussels };

export default function Services() {
  const { t } = useLanguage();
  useSeo(
    "Chauffeur Services — Airport, EU, Corporate | MIRAL GROUP",
    "Airport transfers, diplomatic & EU transport, corporate subscriptions and VIP events — premium Tesla chauffeur services in Antwerp, 24/24."
  );

  return (
    <main className="pt-[72px]">
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <p className="eyebrow">{t.servicesPage.eyebrow}</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">
              {t.servicesPage.title}
            </h1>
            <p className="mt-6 max-w-[60ch] text-lg text-slate-300">{t.servicesPage.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-8">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 grid gap-6 md:grid-cols-2">
          {t.servicesPage.items.map((s, i) => {
            const Icon = SERVICE_ICONS[s.id];
            return (
              <Reveal key={s.id} delay={i * 0.08}>
                <article
                  data-testid={`services-page-item-${s.id}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[#0F1015] transition-colors duration-500 hover:border-gold/40"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={SERVICE_IMAGES[s.id]}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F1015] via-[#0F1015]/20 to-transparent" />
                    <div className="absolute bottom-4 left-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-black/55 backdrop-blur">
                      <Icon size={19} className="text-gold" />
                    </div>
                  </div>
                  <div className="p-6 lg:p-8">
                    <p className="font-mono2 text-xs text-gold/70">0{i + 1}</p>
                    <h2 className="mt-2 font-display text-xl lg:text-2xl font-bold text-white">{s.title}</h2>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#090a0e] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <p className="eyebrow">{t.servicesPage.processEyebrow}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
              {t.servicesPage.processTitle}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {t.servicesPage.steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="relative border-t-2 border-gold/30 pt-6" data-testid={`service-step-${i}`}>
                  <span className="font-mono2 text-4xl text-gold/50">{`0${i + 1}`}</span>
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <div className="rounded-2xl border border-gold/25 bg-gradient-to-br from-[#12141C] to-[#0a0b0e] p-10 lg:p-16 text-center">
              <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t.servicesPage.ctaTitle}</h2>
              <p className="mt-3 text-slate-400">{t.servicesPage.ctaSub}</p>
              <Link to="/contact" data-testid="services-cta" className="btn-gold mt-8 text-xs font-mono2 uppercase tracking-[0.18em]">
                {t.servicesPage.ctaButton} <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
